import { useEffect, useState } from 'react';
import { AnalyticsConfig } from '@24i/nxg-sdk-photon';

import { useFeature } from '../../../context/AppSettingsData';
import { getInitialURL, watchLinkingURL } from './getDeeplinkURL';
import {
    canSaveCampaign,
    canSendCampaign,
    createCampaign,
    createMetaCookie,
    createMetaCookieFromCampaign,
    detectAllowTracking,
    extractCampaignTtlFromConfig,
    isCampaignValid,
    isConversionUrl,
    watchConsentStatus,
} from './utils';
import {
    getValidCampaignSync,
    loadCampaign,
    loadValidCampaign,
    markInitialUrlAsProcessed,
    notifyWatchers,
    registerCampaignWatcher,
    removeCampaign,
    saveCampaign,
    wasInitialUrlAlreadyProcessed,
} from './persistence';
import { setMetaCookie } from './setMetaCookie';
import { CampaignAnalytics, SavedCampaign } from './types';

export const detectAndPersistCampaignUrl = async (
    analyticsConfig: AnalyticsConfig | undefined,
    explicitUrl?: string | null | undefined
): Promise<void> => {
    const campaignTtl = extractCampaignTtlFromConfig(analyticsConfig);
    const allowTracking = await detectAllowTracking();
    if (!campaignTtl || !canSaveCampaign(allowTracking)) {
        await removeCampaign();
    } else {
        const initialURL = await getInitialURL();
        let deeplinkUrl: string | null | undefined = explicitUrl;
        if (!deeplinkUrl && !wasInitialUrlAlreadyProcessed(initialURL)) {
            deeplinkUrl = initialURL;
        }
        markInitialUrlAsProcessed(initialURL);
        if (deeplinkUrl && isConversionUrl(deeplinkUrl)) {
            const creationTime = Date.now();
            const metaCookie = createMetaCookie(deeplinkUrl, creationTime, campaignTtl);
            const savedCampaign = createCampaign(
                deeplinkUrl,
                metaCookie,
                allowTracking,
                creationTime,
                campaignTtl
            );
            await saveCampaign(savedCampaign);
            if (canSendCampaign(savedCampaign.allowTracking) && metaCookie) {
                setMetaCookie(metaCookie);
            }
        }
    }
};

export const notifyConsentChange = async (): Promise<void> => {
    const campaign = await loadCampaign();
    if (!campaign) return; // nothing saved
    if (campaign.expires < Date.now() || !canSaveCampaign(campaign.allowTracking)) {
        await removeCampaign();
    } else if (canSendCampaign(campaign.allowTracking)) {
        notifyWatchers(campaign);
        const metaCookie = createMetaCookieFromCampaign(campaign);
        if (metaCookie) {
            setMetaCookie(metaCookie);
        }
    }
};

const MAX_EXPIRATION_WAIT = 10 * 60 * 1000;

interface CampaignAnalyticsState {
    campaign: SavedCampaign | undefined;
    analytics: CampaignAnalytics;
}

const createAnalyticsState = (campaign: SavedCampaign | undefined): CampaignAnalyticsState => ({
    campaign,
    analytics: {
        campaignUrl: campaign?.url,
        campaignCookie: campaign?.cookie,
    },
});

const emptyAnalytics: CampaignAnalytics = {
    campaignUrl: undefined,
    campaignCookie: undefined,
};

export const useCampaignAnalytics = (): CampaignAnalytics => {
    const [analyticsState, setAnalyticsState] = useState<CampaignAnalyticsState>(
        createAnalyticsState(getValidCampaignSync())
    );
    useEffect(() => {
        let cancelled = false;
        let timeoutId: ReturnType<typeof setTimeout>;
        const safeSetCampaign = (newCampaign: SavedCampaign | undefined): void => {
            if (!cancelled) {
                setAnalyticsState(createAnalyticsState(newCampaign));
                const expiresIn = newCampaign ? newCampaign.expires - Date.now() : 0;
                if (expiresIn > 0 && expiresIn < MAX_EXPIRATION_WAIT) {
                    clearTimeout(timeoutId);
                    timeoutId = setTimeout(() => safeSetCampaign(undefined), expiresIn);
                }
            }
        };
        loadValidCampaign().then(safeSetCampaign);
        const unregister = registerCampaignWatcher(safeSetCampaign);
        return () => {
            cancelled = true;
            unregister();
            clearTimeout(timeoutId);
        };
    }, []);
    return isCampaignValid(analyticsState.campaign) ? analyticsState.analytics : emptyAnalytics;
};

export const useCampaignDetection = () => {
    const analyticsConfigs: AnalyticsConfig[] | undefined = useFeature('analytics');
    const config24iq = analyticsConfigs?.find((a) => a.name === '24iQ');
    useEffect(() => {
        detectAndPersistCampaignUrl(config24iq);
        return watchLinkingURL((url) => detectAndPersistCampaignUrl(config24iq, url));
    }, [config24iq]);
    useEffect(() => watchConsentStatus(notifyConsentChange), []);
};
